if (!customElements.get('maison-sticky-atc')) {
  customElements.define(
    'maison-sticky-atc',
    class MaisonStickyAtc extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.productFormId);
        this.section = this.closest('[id^="MainProduct-"]');
        this.button = this.querySelector('[data-maison-sticky-atc-submit]');
        this.label = this.querySelector('[data-maison-sticky-atc-label]');
        this.price = this.querySelector('[data-maison-sticky-atc-price]');
        this.buttonPrice = this.querySelector('[data-maison-sticky-atc-button-price]');
        this.variantSelect = this.querySelector('[data-maison-sticky-atc-select]');
        this.footer = document.querySelector(this.dataset.footerSelector || '.maison-footer');
        this.header = document.querySelector(this.dataset.headerSelector || '.shopify-section-header-sticky');
        this.frame = null;
        this.pendingVariantId = null;
        this.pendingVariantTimer = null;

        if (!this.form || !this.section || !this.button || !this.primaryButton) {
          this.hide();
          return;
        }

        this.onViewportChange = this.onViewportChange.bind(this);
        this.onProductChange = this.onProductChange.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onStickyVariantChange = this.onStickyVariantChange.bind(this);

        window.addEventListener('scroll', this.onViewportChange, { passive: true });
        window.addEventListener('resize', this.onViewportChange, { passive: true });
        this.section.addEventListener('change', this.onProductChange);
        this.form.addEventListener('submit', this.onFormSubmit);
        this.variantSelect?.addEventListener('change', this.onStickyVariantChange);

        const productInfo = this.section.querySelector('product-info');
        if (productInfo) {
          this.productObserver = new MutationObserver(() => {
            this.syncFromProduct();
            this.onViewportChange();
          });
          this.productObserver.observe(productInfo, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'class'],
            childList: true,
            subtree: true,
          });
        }

        this.syncFromProduct();
        this.onViewportChange();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onViewportChange);
        window.removeEventListener('resize', this.onViewportChange);
        this.section?.removeEventListener('change', this.onProductChange);
        this.form?.removeEventListener('submit', this.onFormSubmit);
        this.variantSelect?.removeEventListener('change', this.onStickyVariantChange);
        this.productObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.pendingVariantTimer) window.clearTimeout(this.pendingVariantTimer);
      }

      get primaryButton() {
        return document.getElementById(this.dataset.primaryButtonId);
      }

      onProductChange(event) {
        if (!event.target?.matches('variant-radios input, variant-selects select, select[name="id"]')) return;
        requestAnimationFrame(() => this.syncFromProduct());
        window.setTimeout(() => this.syncFromProduct(), 180);
        window.setTimeout(() => this.syncFromProduct(), 700);
      }

      onStickyVariantChange(event) {
        const selectedOption = event.currentTarget.selectedOptions?.[0];
        if (!selectedOption) return;

        this.pendingVariantId = String(selectedOption.value);
        if (this.pendingVariantTimer) window.clearTimeout(this.pendingVariantTimer);
        this.pendingVariantTimer = window.setTimeout(() => {
          this.pendingVariantId = null;
          this.syncFromProduct();
        }, 3000);

        const optionValues = (selectedOption.dataset.optionValues || '').split('||');
        const radioGroups = [...this.section.querySelectorAll('variant-radios fieldset')];
        const radioTargets = [];

        optionValues.forEach((value, index) => {
          const group = radioGroups[index];
          const input = group && [...group.querySelectorAll('input[type="radio"]')].find((radio) => radio.value === value);
          if (input) {
            input.checked = true;
            radioTargets.push(input);
          }
        });

        if (radioTargets.length === optionValues.length && radioTargets.length) {
          radioTargets[radioTargets.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const nativeSelects = [...this.section.querySelectorAll('variant-selects select:not([name="id"])')];
        const selectTargets = [];
        optionValues.forEach((value, index) => {
          const select = nativeSelects[index];
          if (select && [...select.options].some((option) => option.value === value)) {
            select.value = value;
            selectTargets.push(select);
          }
        });

        if (selectTargets.length === optionValues.length && selectTargets.length) {
          selectTargets[selectTargets.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onFormSubmit() {
        requestAnimationFrame(() => this.syncFromProduct());
      }

      onViewportChange() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateVisibility();
        });
      }

      syncFromProduct() {
        const primary = this.primaryButton;
        if (!primary) {
          this.hide();
          return;
        }

        const primaryLabel = Array.from(primary.children).find(
          (child) => child.tagName === 'SPAN' && !child.classList.contains('maison-atc-dot') && !child.classList.contains('maison-atc-price')
        );
        if (primaryLabel && this.label) this.label.textContent = primaryLabel.textContent.trim();

        const primaryPrice = primary.querySelector('.maison-atc-price');
        if (primaryPrice) {
          [this.price, this.buttonPrice].forEach((target) => {
            if (!target) return;
            if (target.innerHTML !== primaryPrice.innerHTML) target.innerHTML = primaryPrice.innerHTML;
            target.dataset.productId = primaryPrice.dataset.productId || '';
            target.dataset.variantId = primaryPrice.dataset.variantId || '';
            target.toggleAttribute('data-maison-plan-active', primaryPrice.hasAttribute('data-maison-plan-active'));
          });
        }

        const formVariantId = String(this.form.querySelector('[name="id"]')?.value || '');
        if (this.variantSelect && formVariantId) {
          if (this.pendingVariantId === formVariantId) {
            this.pendingVariantId = null;
            if (this.pendingVariantTimer) window.clearTimeout(this.pendingVariantTimer);
          }
          if (!this.pendingVariantId && [...this.variantSelect.options].some((option) => option.value === formVariantId)) {
            this.variantSelect.value = formVariantId;
          }
        }

        const loading = primary.classList.contains('loading');
        const unavailable = primary.disabled || primary.getAttribute('aria-disabled') === 'true';
        this.button.disabled = unavailable;
        this.button.toggleAttribute('aria-disabled', unavailable);
        this.button.classList.toggle('is-loading', loading);
        if (this.variantSelect) this.variantSelect.disabled = loading;
        this.button.setAttribute(
          'aria-label',
          [this.label?.textContent.trim(), this.buttonPrice?.textContent.trim()].filter(Boolean).join(' — ')
        );
      }

      updateVisibility() {
        const primary = this.primaryButton;
        if (!primary || !this.form?.isConnected) {
          this.hide();
          return;
        }

        const primaryRect = primary.getBoundingClientRect();
        const headerRect = this.header?.getBoundingClientRect();
        const obscuredTop = headerRect && headerRect.bottom > 0 ? headerRect.bottom : 0;
        const primaryHasPassed = primaryRect.bottom <= obscuredTop;

        const footerRect = this.footer?.getBoundingClientRect();
        const footerIsEntering = Boolean(
          footerRect && footerRect.top < window.innerHeight && footerRect.bottom > 0
        );

        this.toggle(primaryHasPassed && !footerIsEntering);
      }

      toggle(visible) {
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        this.inert = !visible;
      }

      hide() {
        this.toggle(false);
      }
    }
  );
}
